export type ReframingPracticeMode =
  | 'identify'
  | 'context_reframe'
  | 'meaning_reframe'
  | 'intention_behaviour'
  | 'coaching_simulation'
  | 'mixed'
  | 'assessment';

export type ReframingDifficulty = 'beginner' | 'intermediate' | 'advanced';

export type ReframingContext =
  | 'coaching'
  | 'relationships'
  | 'leadership'
  | 'education'
  | 'sales'
  | 'self_coaching'
  | 'mixed';

export type ReframingFeedbackTiming =
  | 'after_every'
  | 'after_three'
  | 'when_requested'
  | 'end_only';

export type ReframingSessionLength = 5 | 10 | 20;

export type ReframingInteractionMode = 'voice' | 'chat';

export const REFRAMING_PRACTICE_MODES: {
  id: ReframingPracticeMode;
  label: string;
  description: string;
  useQuestions: boolean;
}[] = [
  {
    id: 'identify',
    label: 'Identify the Reframe',
    description: 'The AI presents a statement and you identify whether it calls for a context or meaning reframe.',
    useQuestions: true,
  },
  {
    id: 'context_reframe',
    label: 'Create Context Reframes',
    description: 'The AI provides an "I am too…" statement and you find a context where the same quality is valued.',
    useQuestions: true,
  },
  {
    id: 'meaning_reframe',
    label: 'Create Meaning Reframes',
    description: 'The AI provides a statement with a fixed interpretation and you offer a plausible alternative meaning.',
    useQuestions: true,
  },
  {
    id: 'intention_behaviour',
    label: 'Separate Intention From Behaviour',
    description: 'The AI presents a behaviour and you identify a possible positive intention and a more effective alternative.',
    useQuestions: true,
  },
  {
    id: 'coaching_simulation',
    label: 'Coaching Simulation',
    description: 'The AI plays a realistic coaching client. You play the practitioner and use reframing in context.',
    useQuestions: false,
  },
  {
    id: 'mixed',
    label: 'Mixed Practice',
    description: 'A mix of all practice types, weighted towards your areas for development.',
    useQuestions: true,
  },
  {
    id: 'assessment',
    label: 'Assessment',
    description: 'A formal assessment with no hints. Balanced mix of practice types. Results at the end only.',
    useQuestions: true,
  },
];

export const REFRAMING_DIFFICULTIES: { id: ReframingDifficulty; label: string; description: string }[] = [
  {
    id: 'beginner',
    label: 'Beginner',
    description: 'Obvious examples, immediate feedback, optional hints.',
  },
  {
    id: 'intermediate',
    label: 'Intermediate',
    description: 'More natural language, some ambiguity, you explain your reasoning.',
  },
  {
    id: 'advanced',
    label: 'Advanced',
    description: 'Complex realistic scenarios, no automatic hints, client may resist or misunderstand.',
  },
];

export const REFRAMING_CONTEXTS: { id: ReframingContext; label: string }[] = [
  { id: 'coaching', label: 'Coaching' },
  { id: 'relationships', label: 'Relationships' },
  { id: 'leadership', label: 'Leadership' },
  { id: 'education', label: 'Education' },
  { id: 'sales', label: 'Sales' },
  { id: 'self_coaching', label: 'Self-Coaching' },
  { id: 'mixed', label: 'Mixed Contexts' },
];

export const REFRAMING_FEEDBACK_TIMINGS: { id: ReframingFeedbackTiming; label: string }[] = [
  { id: 'after_every', label: 'After Every Response' },
  { id: 'after_three', label: 'After Every Three Responses' },
  { id: 'when_requested', label: 'When I Request It' },
  { id: 'end_only', label: 'End of Session Only' },
];

export const REFRAMING_SESSION_LENGTHS: { id: ReframingSessionLength; label: string }[] = [
  { id: 5, label: '5 questions' },
  { id: 10, label: '10 questions' },
  { id: 20, label: '20 questions' },
];

export const REFRAMING_SIMULATION_DURATIONS: { id: ReframingSessionLength; label: string }[] = [
  { id: 5, label: '5 minutes' },
  { id: 10, label: '10 minutes' },
  { id: 20, label: '20 minutes' },
];
